import { Link } from "react-router-dom";
import { ProductDetail } from "@/mocks/productDetails";
import { useThemeColor } from "@/hooks/useThemeColor";

interface RelatedProductsProps {
  relatedIds: number[];
  currentId: number;
  allProducts: ProductDetail[];
}

export default function RelatedProducts({ relatedIds, currentId, allProducts }: RelatedProductsProps) {
  const { primary, primaryLight: light, hexAlpha } = useThemeColor();

  const related = relatedIds
    .filter((id) => id !== currentId)
    .map((id) => allProducts.find((p) => p.id === id))
    .filter(Boolean) as ProductDetail[];

  const fallback = related.length === 0
    ? allProducts.filter(p => p.id !== currentId).slice(0, 3)
    : related;

  if (fallback.length === 0) return null;

  return (
    <section className="mt-20 pt-16" style={{ borderTop: `1px solid ${hexAlpha(primary, 0.1)}` }} dir="rtl">
      <div className="text-center mb-12">
        <p
          className="text-xs tracking-[0.3em] uppercase mb-3"
          style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.7) }}
        >
          قد يعجبك أيضاً
        </p>
        <h2
          className="text-3xl md:text-4xl font-bold text-white"
          style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}
        >
          منتجات ذات صلة
        </h2>
        <div className="w-16 h-0.5 mx-auto mt-4" style={{ background: `linear-gradient(to right, transparent, ${primary}, transparent)` }}></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {fallback.map((product) => {
          const discount = product.originalPrice > product.price
            ? Math.round((1 - product.price / product.originalPrice) * 100)
            : 0;
          return (
            <Link
              key={product.id}
              to={`/product/${product.id}`}
              className="group bg-[#111] border border-white/5 rounded-2xl overflow-hidden block cursor-pointer transition-all duration-300"
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.3);
                (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px ${hexAlpha(primary, 0.08)}`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)";
                (e.currentTarget as HTMLElement).style.boxShadow = "none";
              }}
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden bg-[#0f0f0f]">
                {product.images?.[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.nameAr}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-gray-700 text-3xl"></i>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
                {product.badge && (
                  <span
                    className="absolute top-3 right-3 text-black text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap"
                    style={{ fontFamily: "'Tajawal', sans-serif", background: `linear-gradient(to right, ${light}, ${primary})` }}
                  >
                    {product.badge}
                  </span>
                )}
                {discount > 0 && (
                  <span
                    className="absolute top-3 left-3 bg-red-500/90 text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap"
                    style={{ fontFamily: "'Tajawal', sans-serif" }}
                  >
                    -{discount}%
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-5">
                <p className="text-xs mb-1.5 tracking-wide" style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.5) }}>
                  {product.category}
                </p>
                <h3
                  className="text-white font-bold text-base mb-3 leading-snug transition-colors group-hover:opacity-80"
                  style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}
                >
                  {product.nameAr}
                </h3>
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-xs" style={{ color: i < product.rating ? primary : "#374151" }}></i>
                  ))}
                  <span className="text-gray-500 text-xs mr-1" style={{ fontFamily: "'Tajawal', sans-serif" }}>({product.reviews})</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="font-bold text-lg"
                      style={{
                        fontFamily: "'Amiri', 'Tajawal', serif",
                        background: `linear-gradient(to left, ${light}, ${primary})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {product.price.toLocaleString()} ر.س
                    </span>
                    {product.originalPrice > product.price && (
                      <span className="text-gray-600 text-xs line-through" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                        {product.originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-full border transition-all"
                    style={{ color: primary, borderColor: hexAlpha(primary, 0.3) }}
                  >
                    <i className="ri-arrow-left-line text-sm"></i>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
